import { err, ok, type Result } from "neverthrow";
import type z from "zod";
import { getBackendURL } from "./getBaseUrl";

type RequestConfig<ResponseSchema extends z.ZodType | undefined> = {
  responseSchema: ResponseSchema;
  request: Request;
  includeProjectId?: boolean;
};

type BuildOptions = {
  projectId?: string;
  organizationId?: string;
};

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS" | "HEAD";

export class RequestBuilder<
  PathParamsSchema extends z.ZodType | undefined = undefined,
  BodySchema extends z.ZodType | undefined = undefined,
  ResponseSchema extends z.ZodType | undefined = undefined,
> {
  private pathParamsSchema?: PathParamsSchema = undefined;
  private bodySchema?: BodySchema = undefined;
  private responseSchema?: ResponseSchema = undefined;

  private method: Method = "GET";
  private headers: Headers = new Headers();
  private path: (query: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined) => string;
  private queryParamsFn?: (
    params: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined
  ) => Record<string, string> | undefined;
  private includeProjectId = false;
  private includeOrganizationId = false;

  constructor(opts: {
    path: (query: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined) => string;
    paramsSchema?: PathParamsSchema;
    bodySchema?: BodySchema;
    responseSchema?: ResponseSchema;
  }) {
    this.pathParamsSchema = opts.paramsSchema;
    this.bodySchema = opts.bodySchema;
    this.responseSchema = opts.responseSchema;
    this.path = opts.path;

    this.withContentType("application/json");
  }

  withMethod(method: Method) {
    this.method = method;
    return this;
  }

  withContentType(contentType: "application/json" | undefined) {
    if (!contentType) {
      return this.withRemoveHeader("Content-Type");
    }
    return this.withHeader("Content-Type", contentType);
  }

  withRemoveHeader(name: string) {
    this.headers.delete(name);
    return this;
  }

  withHeader(name: string, value: string) {
    this.headers.set(name, value);
    return this;
  }

  withBearerToken(token: string | undefined) {
    if (!token) {
      return this;
    }
    return this.withHeader("Authorization", `Bearer ${token}`);
  }

  withQueryParams(
    fn: (
      params: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined
    ) => Record<string, string> | undefined
  ) {
    this.queryParamsFn = fn;
    return this;
  }

  withIncludeProjectId() {
    this.includeProjectId = true;
    return this;
  }

  withIncludeOrganizationId() {
    this.includeOrganizationId = true;
    return this;
  }

  build(
    params: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined,
    body: BodySchema extends z.ZodType ? z.infer<BodySchema> : undefined,
    options?: BuildOptions
  ): Result<RequestConfig<ResponseSchema>, Error> {
    if (this.bodySchema) {
      const p = this.bodySchema.safeParse(body);
      if (!p.success) {
        return err(new Error(`Invalid body: ${p.error.message}`));
      }
    }
    if (this.pathParamsSchema) {
      const p = this.pathParamsSchema.safeParse(params);
      if (!p.success) {
        return err(new Error(`Invalid params: ${p.error.message}`));
      }
    }

    // Create a fresh URL for each build to avoid state pollution
    const url = new URL(getBackendURL());
    url.pathname = this.path(params);

    // Apply query params if provided
    if (this.queryParamsFn) {
      const queryParams = this.queryParamsFn(params);
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
      }
    }

    const requestHeaders = new Headers(this.headers);

    if (this.includeProjectId && options?.projectId) {
      requestHeaders.set("X-Project-Id", options.projectId);
    }

    if (this.includeOrganizationId && options?.organizationId) {
      requestHeaders.set("X-Organization-Id", options.organizationId);
    }

    return ok({
      responseSchema: this.responseSchema as ResponseSchema,
      request: new Request(url.toString(), {
        method: this.method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
      }),
      includeProjectId: this.includeProjectId,
    });
  }
}
